import type { RealtimeChannel } from '@supabase/realtime-js'
import { sharedRealtimeClient } from './realtimeClient'
import realtimeLogger from './realtimeLogger'

type MaintenanceEventType =
  | 'maintenance:created'
  | 'maintenance:deleted'
  | 'maintenance:activated'
  | 'maintenance:ended'
  | 'maintenance:updated'

export type MaintenancePayload = {
  id: number
  suspension_type: 'full' | 'restricted'
  suspend_from: string
  suspend_to: string
  reason: string | null
  status: string
  event_type: MaintenanceEventType
}

export type MaintenanceListener = (payload: MaintenancePayload) => void

const MAINTENANCE_EVENTS: MaintenanceEventType[] = [
  'maintenance:created',
  'maintenance:deleted',
  'maintenance:activated',
  'maintenance:ended',
  'maintenance:updated',
]

class MaintenanceManager {
  private channel: RealtimeChannel | null = null
  private listeners = new Set<MaintenanceListener>()
  private isSubscribed = false

  /**
   * Subscribe to maintenance channel events.
   * On activation/ended events, dispatches custom DOM events for App.tsx consumption.
   */
  async subscribe(listener?: MaintenanceListener): Promise<void> {
    if (listener) {
      this.listeners.add(listener)
    }

    if (this.isSubscribed) {
      console.log('[realtimeMaintenance] Already subscribed to maintenance channel')
      return
    }

    const client = await sharedRealtimeClient.getClient()
    if (!client) {
      console.warn('[realtimeMaintenance] No realtime client available, skipping subscription')
      return
    }

    console.log('[realtimeMaintenance] Subscribing to maintenance channel')

    this.channel = client.channel('maintenance', {
      config: {
        broadcast: {
          ack: false,
          self: false,
        },
      },
    })

    // Listen for each event type
    for (const eventType of MAINTENANCE_EVENTS) {
      this.channel.on('broadcast', { event: eventType }, ({ payload }) => {
        const maintenancePayload = payload as MaintenancePayload
        console.log(`[realtimeMaintenance] Event received: ${eventType}`, maintenancePayload)
        realtimeLogger.logEventReceived('maintenance', eventType, maintenancePayload)

        // Dispatch DOM events for App.tsx to handle
        if (eventType === 'maintenance:activated') {
          window.dispatchEvent(
            new CustomEvent('maintenance:activated', { detail: maintenancePayload })
          )
        } else if (eventType === 'maintenance:ended') {
          window.dispatchEvent(
            new CustomEvent('maintenance:ended', { detail: maintenancePayload })
          )
        }

        // Emit to registered listeners (for Phase 48 countdown banner, etc.)
        this.emit(maintenancePayload)
      })
    }

    this.channel.subscribe((status) => {
      if (status === 'CHANNEL_ERROR') {
        console.warn('[realtimeMaintenance] Channel error on maintenance channel')
      }
      if (status === 'SUBSCRIBED') {
        console.log('[realtimeMaintenance] Subscribed to maintenance channel')
        this.isSubscribed = true
        realtimeLogger.logSubscription('maintenance', 'maintenance-schedule')
      }
      if (status === 'CLOSED') {
        console.log('[realtimeMaintenance] Maintenance channel closed')
        this.isSubscribed = false
        this.channel = null
      }
    })
  }

  /**
   * Emit payload to all registered listeners
   */
  private emit(payload: MaintenancePayload): void {
    Array.from(this.listeners).forEach((listener) => {
      try {
        listener(payload)
      } catch (error) {
        console.error('[realtimeMaintenance] Listener error:', error)
        realtimeLogger.logEventError('maintenance', payload.event_type, error as Error)
      }
    })
  }

  /**
   * Unsubscribe from maintenance channel and clean up
   */
  unsubscribe(): void {
    if (this.channel) {
      this.channel.unsubscribe()
      this.channel = null
      console.log('[realtimeMaintenance] Unsubscribed from maintenance channel')
      realtimeLogger.logUnsubscription('maintenance', 'Manual unsubscribe')
    }
    this.listeners.clear()
    this.isSubscribed = false
  }

  /**
   * Remove a specific listener
   */
  removeListener(listener: MaintenanceListener): void {
    this.listeners.delete(listener)
  }
}

// Singleton instance
const maintenanceManager = new MaintenanceManager()

// Convenience functions
export const subscribeToMaintenance = (listener?: MaintenanceListener): void => {
  maintenanceManager.subscribe(listener).catch((error) => {
    console.error('[realtimeMaintenance] Failed to subscribe:', error)
  })
}

export const unsubscribeFromMaintenance = (): void => {
  maintenanceManager.unsubscribe()
}

export default maintenanceManager
